import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import * as QueryKeys from "data";
import * as localStorageKeys from "data/local-storage-keys";
import { DatasetDefinitions } from "data/models";
import { fetchDefinitions } from "data/queries-common";
import {
  CATALOGUE_THEMES,
  getPopularityRank,
  searchCatalogue,
} from "content/catalogue";
import { useLocalStorage } from "utils/local-storage";
import { useMemo } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { CatalogueCard } from "./catalogue-card";
import { CatalogueListItem } from "./catalogue-list-item";
import {
  CatalogueControls,
  CatalogueSort,
  CatalogueView,
  FacetKey,
  FacetSelection,
} from "./catalogue-controls";
import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { BookOpen } from "lucide-react";

const FACET_KEYS: FacetKey[] = ["theme", "format", "region"];

const SORT_VALUES: CatalogueSort[] = [
  "popular",
  "relevance",
  "updated",
  "az",
  "za",
];

const emptySelection = (): FacetSelection => ({
  theme: new Set(),
  format: new Set(),
  region: new Set(),
});

// Search, sort and filters live in the URL so a catalogue view can be shared
// by copying the address. View is a personal preference and stays local.
const parseCatalogueState = (search: string) => {
  const params = new URLSearchParams(search);
  const sortParam = params.get("sort") as CatalogueSort | null;
  const selected = emptySelection();
  // Repeated params (?theme=A&theme=B) so facet values may contain commas.
  for (const facet of FACET_KEYS) {
    for (const value of params.getAll(facet)) {
      if (value) selected[facet].add(value);
    }
  }
  return {
    query: params.get("q") ?? "",
    sort: sortParam && SORT_VALUES.includes(sortParam) ? sortParam : "popular",
    selected,
  };
};

const buildCatalogueSearch = (
  query: string,
  sort: CatalogueSort,
  selected: FacetSelection,
) => {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (sort !== "popular") params.set("sort", sort);
  for (const facet of FACET_KEYS) {
    for (const value of selected[facet]) params.append(facet, value);
  }
  const search = params.toString();
  return search ? `?${search}` : "";
};

export const CataloguePage = () => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [view, setView] = useLocalStorage<CatalogueView>(
    localStorageKeys.catalogueView,
    "grid",
  );

  const { query, sort, selected } = useMemo(
    () => parseCatalogueState(search),
    [search],
  );

  const applyState = (next: {
    query?: string;
    sort?: CatalogueSort;
    selected?: FacetSelection;
  }) => {
    history.replace(
      `${pathname}${buildCatalogueSearch(
        next.query ?? query,
        next.sort ?? sort,
        next.selected ?? selected,
      )}`,
    );
  };

  // Release dates, formats and regions are fetched from DATAPI dynamically.
  const datasetDefinitions: UseQueryResult<DatasetDefinitions> = useQuery({
    queryKey: [QueryKeys.definitionsKey],
    queryFn: () => fetchDefinitions(),
  });
  const definitions = datasetDefinitions.data;

  const metadataFor = (code: string) => definitions?.metadata[code];

  // Formats and regions come from the datasets API, so only offer the ones the
  // catalogue's own products actually have. Options don't depend on search.
  const { formatOptions, regionOptions } = useMemo(() => {
    const formats = new Set<string>();
    const regions = new Set<string>();
    for (const entry of searchCatalogue("")) {
      const meta = definitions?.metadata[entry.datasetProductCode];
      meta?.formats.forEach((f) => formats.add(f));
      meta?.regions.forEach((r) => regions.add(r));
    }
    return {
      formatOptions: [...formats].sort(),
      regionOptions: [...regions].sort(),
    };
  }, [definitions]);

  const entries = useMemo(() => {
    const filtered = searchCatalogue(query).filter((entry) => {
      if (selected.theme.size && !selected.theme.has(entry.metadata.theme)) {
        return false;
      }
      const meta = definitions?.metadata[entry.datasetProductCode];
      if (
        selected.format.size &&
        !meta?.formats.some((f) => selected.format.has(f))
      ) {
        return false;
      }
      if (
        selected.region.size &&
        !meta?.regions.some((r) => selected.region.has(r))
      ) {
        return false;
      }
      return true;
    });

    const byName = (a: typeof filtered[number], b: typeof filtered[number]) =>
      a.displayName.localeCompare(b.displayName);
    const byPopularity = (
      a: typeof filtered[number],
      b: typeof filtered[number],
    ) =>
      getPopularityRank(a.datasetProductCode) -
        getPopularityRank(b.datasetProductCode) || byName(a, b);

    const sorted = [...filtered];
    switch (sort) {
      case "az":
        sorted.sort(byName);
        break;
      case "za":
        sorted.sort((a, b) => byName(b, a));
        break;
      case "updated":
        sorted.sort((a, b) => {
          const at = definitions?.metadata[a.datasetProductCode]?.latestRelease;
          const bt = definitions?.metadata[b.datasetProductCode]?.latestRelease;
          return (
            (bt ? dayjs(bt).valueOf() : -Infinity) -
              (at ? dayjs(at).valueOf() : -Infinity) || byPopularity(a, b)
          );
        });
        break;
      case "relevance": {
        const q = query.trim().toLowerCase();
        if (!q) {
          sorted.sort(byPopularity);
          break;
        }
        const rank = (name: string) => {
          const n = name.toLowerCase();
          return n.startsWith(q) ? 0 : n.includes(q) ? 1 : 2;
        };
        sorted.sort(
          (a, b) =>
            rank(a.displayName) - rank(b.displayName) || byPopularity(a, b),
        );
        break;
      }
      default:
        sorted.sort(byPopularity);
    }
    return sorted;
  }, [query, sort, selected, definitions]);

  const toggleFacet = (facet: FacetKey, value: string) => {
    const next = new Set(selected[facet]);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    applyState({ selected: { ...selected, [facet]: next } });
  };

  return (
    <StandardPageLayout
      pageTitle={{
        icon: BookOpen,
        title: "Data Catalogue",
        description:
          "Explore the Geoscape datasets and find the right data for your needs",
      }}
    >
      <CatalogueControls
        query={query}
        onQueryChange={(value) => applyState({ query: value })}
        sort={sort}
        onSortChange={(value) => applyState({ sort: value })}
        view={view}
        onViewChange={setView}
        themeOptions={CATALOGUE_THEMES}
        formatOptions={formatOptions}
        regionOptions={regionOptions}
        facetsPending={!definitions}
        selected={selected}
        onToggle={toggleFacet}
        onClearAll={() => applyState({ selected: emptySelection() })}
      />

      <ReactQueryErrorWrapper queries={[datasetDefinitions]} mutations={[]} />

      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No datasets match your search and filters.
        </p>
      )}

      {view === "grid" ? (
        <div className="grid gap-4 xl:grid-cols-2" data-testid="catalogue-results">
          {entries.map((entry) => (
            <CatalogueCard
              key={entry.slug}
              entry={entry}
              updatedDate={metadataFor(entry.datasetProductCode)?.latestRelease}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="catalogue-results">
          {entries.map((entry) => (
            <CatalogueListItem
              key={entry.slug}
              entry={entry}
              updatedDate={metadataFor(entry.datasetProductCode)?.latestRelease}
            />
          ))}
        </div>
      )}
    </StandardPageLayout>
  );
};
